/**
 * Run `worker` over `items` with at most {@code limit} in flight at a time,
 * preserving result order. Each result records whether the worker resolved
 * (`ok: true`) or rejected (`ok: false`), so a single failing item never
 * brings the whole batch down — useful for multi-file uploads where the
 * caller wants per-file failure UX rather than all-or-nothing semantics.
 *
 * The implementation uses a shared cursor + N parallel "lanes": each lane
 * pulls the next index, awaits the worker, writes into the result slot,
 * then loops. This avoids the classic Promise.all-of-batches pitfall where
 * one slow item blocks an entire chunk.
 */
export async function withConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<
  Array<
    | { item: T; index: number; ok: true; value: R }
    | { item: T; index: number; ok: false; error: unknown }
  >
> {
  const results: Array<
    | { item: T; index: number; ok: true; value: R }
    | { item: T; index: number; ok: false; error: unknown }
  > = new Array(items.length);
  let cursor = 0;
  const lane = async (): Promise<void> => {
    while (cursor < items.length) {
      const i = cursor++;
      const item = items[i] as T;
      try {
        const value = await worker(item, i);
        results[i] = { item, index: i, ok: true, value };
      } catch (error) {
        results[i] = { item, index: i, ok: false, error };
      }
    }
  };
  const laneCount = Math.min(Math.max(1, limit), items.length);
  await Promise.all(Array.from({ length: laneCount }, lane));
  return results;
}
